import {COMPANY_DOCUMENT_TYPES, IDENTITY_DOCUMENT_TYPES} from '@sollapay/domain';
import {DocumentContext, DocumentEntityType} from '@sollapay/enums';
import {useEffect, useMemo, useRef} from 'react';
import {useFieldArray, useForm, useWatch} from 'react-hook-form';

import {useUpsertSellersMutation} from '@/features/trust-accounts/activation/hooks';
import {useTrustTypeStrategy} from '@/features/trust-accounts/providers';
import {useFormShell} from '@/hooks';

import {flattenFieldErrorsByPath} from '../../../utils';
import {createSellersFormSchema} from '../../../validation';
import {EMPTY_SELLER_FORM_VALUES, toSellersFormValues, toUpsertSellersPayload} from '../utils';

import type {SellersFormValues} from '../utils';
import type {FormStateSnapshot} from '@/hooks';
import type {TrustSellerDetailsDTO} from '@sollapay/types';

/** Document types feeding AI extraction for the seller roster — one scope per row, tagged by `sellerId`. */
const SELLER_AI_DOCUMENT_TYPES = [...COMPANY_DOCUMENT_TYPES, ...IDENTITY_DOCUMENT_TYPES];

interface UseSellerDetailsFormParams {
  trustAccountId: string;
  sellers: TrustSellerDetailsDTO[] | undefined;
  isLoading: boolean;
  isError: boolean;
  isReadOnly: boolean;
  canCreateInitialSeller: boolean;
  onFormStateChange?: (state: FormStateSnapshot) => void;
}

export const useSellerDetailsForm = ({
  trustAccountId,
  sellers,
  isLoading,
  isError,
  isReadOnly,
  canCreateInitialSeller,
  onFormStateChange
}: UseSellerDetailsFormParams) => {
  const upsertSellersMutation = useUpsertSellersMutation(trustAccountId);
  const strategy = useTrustTypeStrategy();

  const sellersSchema = useMemo(
    () =>
      createSellersFormSchema({
        asksRelationshipQuestions: strategy.asksSellerSideRelationshipQuestions,
        asksSellerRole: strategy.asksSellerSideRole
      }),
    [strategy.asksSellerSideRelationshipQuestions, strategy.asksSellerSideRole]
  );

  const sellersDefaults = useMemo(
    () => toSellersFormValues(sellers ?? [], !strategy.allowsMultipleSellerSideParties),
    [sellers, strategy.allowsMultipleSellerSideParties]
  );

  const form = useForm<SellersFormValues>({
    defaultValues: sellersDefaults
  });

  const {
    fields: sellerFields,
    append: appendSeller,
    remove: removeSeller
  } = useFieldArray({
    control: form.control,
    name: 'sellers'
  });

  useEffect(() => {
    form.reset(sellersDefaults, {keepDirtyValues: true});
  }, [sellersDefaults, form]);

  /** Persists `values` and syncs the form with the server response. Swallows the error —
   * `saveError` surfaces it, and local state (e.g. an appended row) stays so the user can
   * retry via Save. */
  const persistSellers = async (values: SellersFormValues) => {
    try {
      const result = await upsertSellersMutation.mutateAsync(
        toUpsertSellersPayload(values, strategy)
      );
      const savedSellers = result.data.sellers;
      // keepDirtyValues avoids remounting every row (and desyncing RHF's own validation) on reset.
      form.reset(toSellersFormValues(savedSellers), {keepDirtyValues: true});
      // keepDirtyValues also keeps an appended row's `id: undefined` — backfill the server's ids.
      savedSellers.forEach((seller, index) => {
        if (!form.getValues(`sellers.${index}.id`)) form.setValue(`sellers.${index}.id`, seller.id);
      });
    } catch {
      /* empty */
    }
  };

  const hasCreatedInitialSellerRef = useRef(false);
  useEffect(() => {
    const needsInitialRow = !sellers?.length && sellersDefaults.sellers.length > 0;
    if (!canCreateInitialSeller || isLoading || isError || !needsInitialRow) return;
    if (hasCreatedInitialSellerRef.current) return;
    hasCreatedInitialSellerRef.current = true;
    void persistSellers(sellersDefaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canCreateInitialSeller, isLoading, isError, sellers, sellersDefaults]);

  const handleSave = form.handleSubmit(async values => {
    const result = await upsertSellersMutation.mutateAsync(
      toUpsertSellersPayload(values, strategy)
    );
    // Unlike persistSellers' background auto-save, this is the explicit Save — re-baseline to clean.
    form.reset(toSellersFormValues(result.data.sellers));
  });

  /** Persists the new row immediately so it gets a real id and can accept document uploads
   * right away, instead of waiting for the next explicit Save. */
  const handleAddSeller = async () => {
    appendSeller(EMPTY_SELLER_FORM_VALUES, {shouldFocus: false});
    await persistSellers(form.getValues());
  };

  const handleCancel = () => {
    form.reset(sellersDefaults);
    upsertSellersMutation.reset();
  };

  const allValues = useWatch({control: form.control, defaultValue: sellersDefaults});
  const validationResult = sellersSchema.safeParse(allValues);
  const hasErrors = !validationResult.success;
  const fieldErrors = flattenFieldErrorsByPath(
    validationResult.success ? null : validationResult.error
  );

  const sellerIds = (allValues.sellers ?? []).map(seller => seller?.id);

  /** Unsaved rows own no documents, so skipping them also keeps every scope id unique. */
  const documentScopes = sellerIds
    .filter((sellerId): sellerId is string => !!sellerId)
    .map(sellerId => ({id: sellerId, metadata: {sellerId}}));

  const {
    aiFields,
    processingScopes,
    suggestionsCountByScope,
    markFieldModified,
    handleCancelWithAI,
    stopScope,
    reindexFieldArray
  } = useFormShell<SellersFormValues>({
    isDirty: form.formState.isDirty,
    isLoading,
    isSaving: upsertSellersMutation.isPending,
    isError,
    isSaveError: upsertSellersMutation.isError,
    hasErrors,
    isReadOnly,
    onSave: handleSave,
    onCancel: handleCancel,
    onFormStateChange,
    ai: {
      entityType: DocumentEntityType.TRUST_ACCOUNT,
      entityId: trustAccountId,
      documentContext: DocumentContext.TRUST_DETAILS,
      documentTypes: SELLER_AI_DOCUMENT_TYPES,
      documentScopes,
      getValues: form.getValues,
      setValue: form.setValue,
      // null drops the suggestions when that seller is no longer on the roster.
      resolveField: (targetField, scopeId) => {
        const rowIndex = sellerIds.indexOf(scopeId);
        return rowIndex === -1 ? null : `sellers.${rowIndex}.${targetField}`;
      }
    }
  });

  /** Re-keys AI field paths so badges don't surface on the seller that shifted into the gap. */
  const handleRemoveSeller = (index: number) => {
    removeSeller(index);
    reindexFieldArray({fieldArrayPrefix: 'sellers', removedIndex: index});
  };

  /** Translates row position to scope id, keeping the form and row components index-based. */
  const sellerAiState = (index: number): {isProcessing: boolean; suggestionsCount: number} => {
    const sellerId = sellerIds[index];
    if (!sellerId) return {isProcessing: false, suggestionsCount: 0};

    return {
      isProcessing: processingScopes.has(sellerId),
      suggestionsCount: suggestionsCountByScope.get(sellerId) ?? 0
    };
  };

  const stopSellerAi = (index: number): void => {
    const sellerId = sellerIds[index];
    if (sellerId) stopScope(sellerId);
  };

  return {
    form,
    sellerFields,
    handleAddSeller,
    removeSeller: handleRemoveSeller,
    isAddingSeller: upsertSellersMutation.isPending,
    handleSave,
    handleCancel: handleCancelWithAI,
    saveError: upsertSellersMutation.error,
    aiFields,
    sellerAiState,
    markFieldModified,
    stopSellerAi,
    fieldErrors,
    asksSellerSideRole: strategy.asksSellerSideRole,
    sellerRoleOptions: strategy.getSellerSideRoleOptions(),
    allowsMultipleSellerSideParties: strategy.allowsMultipleSellerSideParties,
    asksSellerSideRelationshipQuestions: strategy.asksSellerSideRelationshipQuestions
  };
};
